import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { logEvent } from "./eventLogger.js";
import "./styles.css";

const MODULE_NAME = "_999_ai_pattern_insights";

const AIPatternInsights = () => {
    const [messages, setMessages] = useState([]); // Currently displayed bubbles
    const [loading, setLoading] = useState(false); // API call in progress
    const [typing, setTyping] = useState(false);   // AI "thinking" animation
    const [error, setError] = useState(null);
    const [hasRequested, setHasRequested] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);
    const [metadata, setMetadata] = useState(null);

    const scrollRef = useRef(null);

    // List of pattern keywords to highlight
    const patternKeywords = [
        "Concentration",
        "Extended Hour",
        "Attachment",
        "Over Trading",
        "First Half",
        "Behavioral",
        "Risk"
    ];

    // Auto-scroll to latest bubble
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, typing]);

    useEffect(() => {
        const animationPlayed = sessionStorage.getItem("ai_insights_anim_played");
        if (!animationPlayed) setShowAnimation(true);
    }, []);

    const handleAnimationFinished = () => {
        setTimeout(() => {
            setShowAnimation(false);
            sessionStorage.setItem("ai_insights_anim_played", "true");
        }, 4000);
    };

    const formatInsightContent = (text) => {
        if (!text) return "";
        return text.replace(/\*\*/g, "").replace(/^\d+\.\s*/, "").trim();
    };

    /**
     * Highlights specific pattern keywords and links trading symbols (2-5 uppercase letters)
     */
    const renderHighlightedText = (text, idx) => {
        if (!text) return "";
        const regex = new RegExp(`(${patternKeywords.join("|")}|\\b[A-Z]{2,5}\\b)`, "g");
        const parts = text.split(regex);

        return parts.map((part, i) => {
            if (!part) return null;

            const isPattern = patternKeywords.some(k => k.toLowerCase() === part.toLowerCase());
            if (isPattern) {
                return (
                    <strong
                        key={i}
                        className="highlight-keyword"
                        onClick={() => {
                            logEvent({
                                event_type: "click",
                                module_name: MODULE_NAME,
                                subcomponent: "pattern_keyword_link",
                                metadata: { keyword: part, insight_index: idx }
                            });
                        }}
                        style={{ cursor: "pointer" }}
                    >
                        {part}
                    </strong>
                );
            }

            if (/^[A-Z]{2,5}$/.test(part)) {
                return (
                    <Link
                        key={i}
                        to={`/pg_tag?symbol=${part}`}
                        className="symbol-link"
                        onClick={() => {
                            sessionStorage.setItem("navigationSymbol", part);
                            logEvent({
                                event_type: "click",
                                module_name: MODULE_NAME,
                                subcomponent: "symbol_link",
                                metadata: { symbol: part, insight_index: idx }
                            });
                        }}
                    >
                        {part}
                    </Link>
                );
            }

            return part;
        });
    };

    // --- Add feedback handler ---
    const handleFeedback = (type) => {
        logEvent({
            event_type: "click",
            module_name: MODULE_NAME,
            subcomponent: "analysis_feedback",
            metadata: { feedback: type }
        });
    };

    const fetchInsights = async () => {
        setLoading(true);
        setError(null);
        setHasRequested(true);
        setMessages([]); // Clear previous sequence

        // 1. User Message (Immediate)
        setMessages([{ role: "user", text: "Analyze my patterns." }]);

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/ai-pattern-insights`,
                { withCredentials: true }
            );

            if (res.data.success) {
                const rawInsights = res.data.insights || "";
                const allAIBubbles = rawInsights
                    .split('\n')
                    .filter(line => line.trim() && !line.startsWith('#'))
                    .map(line => formatInsightContent(line.replace(/^[-•]\s*/, "")))
                    .filter(msg => msg.length > 0);

                setMetadata({ date: res.data.generated_at, tokens: res.data.tokens_used });

                // --- 🎭 START ANIMATION SEQUENCE ---
                setLoading(false);

                // Show first profile insight after 600ms
                setTimeout(() => {
                    if (allAIBubbles[0]) setMessages(prev => [...prev, { role: "ai", text: allAIBubbles[0] }]);

                    // Show second actionable insight after 1.8s
                    setTimeout(() => {
                        if (allAIBubbles[1]) setMessages(prev => [...prev, { role: "ai", text: allAIBubbles[1] }]);

                        // Start "Thinking" for the final concluding thought
                        setTimeout(() => {
                            setTyping(true);

                            // Deliver final bubble after 2.5s of "typing"
                            setTimeout(() => {
                                setTyping(false);
                                if (allAIBubbles[2]) setMessages(prev => [...prev, { role: "ai", text: allAIBubbles[2] }]);
                            }, 2500);
                        }, 1000);
                    }, 1200);
                }, 600);

            } else {
                setError(res.data.error || "Failed to generate insights");
                setLoading(false);
            }
        } catch (err) {
            setError(err.response?.data?.error || "Error fetching insights");
            setLoading(false);
        }
    };

    if (showAnimation) {
        return (
            <div style={{ width: "95%", textAlign: "center", padding: "20px 0" }}>
                <img src="/images/brain-animation.gif" alt="Thinking" style={{ maxWidth: "200px" }} onLoad={handleAnimationFinished} />
            </div>
        );
    }

    return (
        <div data-analytics={MODULE_NAME} className="ai-chat-card">
            <div className="ai-chat-header">
                <span className="ai-badge">CORE BEHAVIOR ANALYSIS</span>
                {hasRequested && !loading && !typing && (
                    <button onClick={fetchInsights} className="btn-chat-refresh">New Scan</button>
                )}
            </div>

            <div className="bubble-container" ref={scrollRef}>
                {!hasRequested && (
                    <div className="welcome-prompt">
                        <p>Ready to process your trade data for behavioral insights.</p>
                        <button
                            onClick={() => {
                                logEvent({
                                    event_type: "click",
                                    module_name: MODULE_NAME,
                                    subcomponent: "get_AI_insights",
                                    metadata: { action: "get_AI_insights" }
                                });
                                fetchInsights();
                            }}
                            className="btn-start-chat"
                        >
                            Get AI Insights 
                        </button>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <div key={idx} className={`bubble-row ${msg.role}`}>
                        {/* No per-bubble thumbs */}
                        <div className="speech-bubble">
                            {msg.role === "ai" ? renderHighlightedText(msg.text, idx) : msg.text}
                        </div>
                    </div>
                ))}

                {(loading || typing) && (
                    <div className="bubble-row ai">
                        <div className="typing-bubble">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                )}

                {error && <div className="chat-error-log">{error}</div>}
            </div>

            {metadata && !typing && (
                <div className="chat-metadata" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    <span>Analysis complete: </span>
                    <button
                        className="thumb-btn thumb-btn-bottom"
                        aria-label="Thumbs up"
                        onClick={() => handleFeedback("up")}
                        style={{ marginLeft: 8 }}
                    >
                        <span role="img" aria-label="Thumbs up" style={{ opacity: 0.5, fontSize: "1.2em" }}>👍</span>
                    </button>
                    <button
                        className="thumb-btn thumb-btn-bottom"
                        aria-label="Thumbs down"
                        onClick={() => handleFeedback("down")}
                        style={{ marginLeft: 4 }}
                    >
                        <span role="img" aria-label="Thumbs down" style={{ opacity: 0.5, fontSize: "1.2em" }}>👎</span>
                    </button>
                </div>
            )}

            <style>{`
                .ai-chat-card {
                    width: 95%; background: #0b1120; border-radius: 12px; margin: 16px auto;
                    border: 1px solid rgba(0, 255, 255, 0.1); 
                    box-shadow: 0 10px 30px rgba(0,0,0,0.5); overflow: hidden;
                }
                .ai-chat-header {
                    padding: 10px 16px; background: rgba(255,255,255,0.02);
                    display: flex; justify-content: space-between; align-items: center;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                }
                .ai-badge { color: #00FFFF; font-size: 0.70rem; font-weight: 900; letter-spacing: 2px; }
                .bubble-container { 
                    padding: 16px; max-height: 400px; overflow-y: auto; 
                    display: flex; flex-direction: column; gap: 12px;
                }
                .bubble-row { display: flex; width: 100%; animation: slideIn 0.3s ease-out forwards; }
                .bubble-row.user { justify-content: flex-end; }
                .bubble-row.ai { justify-content: flex-start; }
                .speech-bubble {
                    max-width: 85%; padding: 12px 16px; font-size: 0.9rem; line-height: 1.5;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
                }
                .user .speech-bubble { background: #1e293b; color: #94a3b8; border-radius: 15px 15px 2px 15px; }
                .ai .speech-bubble { 
                    background: linear-gradient(135deg, #1e3a8a 0%, #1e1b4b 100%); 
                    color: #fff; border-radius: 15px 15px 15px 2px;
                    border: 1px solid rgba(0, 255, 255, 0.1);
                }
                .highlight-keyword {
                    color: #00FFFF;
                    font-weight: 700;
                    text-shadow: 0 0 8px rgba(0, 255, 255, 0.4);
                }
                .symbol-link {
                    color: #4ade80;
                    text-decoration: underline;
                    font-weight: 800;
                    cursor: pointer;
                }
                .symbol-link:hover { color: #fff; text-shadow: 0 0 8px rgba(74, 222, 128, 0.6); }
                .typing-bubble { background: #1e293b; padding: 12px 20px; border-radius: 20px; display: flex; gap: 5px; }
                .typing-bubble span { width: 6px; height: 6px; background: #00FFFF; border-radius: 50%; animation: bounce 1.4s infinite; opacity: 0.4; }
                .typing-bubble span:nth-child(2) { animation-delay: 0.2s; }
                .typing-bubble span:nth-child(3) { animation-delay: 0.4s; }
                @keyframes bounce { 0%, 100% { transform: translateY(0); opacity: 0.4; } 50% { transform: translateY(-4px); opacity: 1; } }
                @keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
                .welcome-prompt { text-align: center; padding: 30px 0; color: #64748b; }
                .btn-start-chat { 
                    margin-top: 15px; background: rgba(0,255,255,0.1); border: 1px solid #00FFFF; 
                    color: #00FFFF; padding: 8px 20px; border-radius: 6px; cursor: pointer; font-weight: bold;
                }
                .chat-metadata { 
                    padding: 8px 16px; 
                    font-size: 0.6rem; 
                    color: #334155; 
                    text-align: right; 
                    display: flex; 
                    align-items: center; 
                    justify-content: flex-end;
                    gap: 0;
                }
                .thumb-btn {
                    background: none;
                    border: none;
                    color: inherit;
                    cursor: pointer;
                    margin: 0;
                    padding: 0 2px;
                    transition: opacity 0.2s;
                }
                .thumb-btn-bottom span {
                    opacity: 0.5;
                    font-size: 1.2em;
                    transition: opacity 0.2s;
                }
                .thumb-btn-bottom:hover span {
                    opacity: 1;
                }
                       .btn-chat-refresh { background: transparent; border: 1px solid #4ade80; color: #4ade80; padding: 3px 8px; font-size: 0.65rem; border-radius: 4px; cursor: pointer; }

            `}</style>
        </div>
    );
};

export default AIPatternInsights;